import { useEffect, useMemo } from 'react';

import { isAfter, isBefore } from 'date-fns';

import { IChannelExclusions, IMainMenuSectionView, IMainMenuViewOption } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { IDayPartBoundary } from 'state/day-part/hooks/use-active-day-parts';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DAY_PART_SELECTIONS } from 'state/main-menu/types';
import { useMenuContext } from 'state/menu';
import { ServiceMode } from 'state/service-mode';
import { isKiosk } from 'utils/environment';
import { isPickup } from 'utils/service-mode/service-mode';

import { IDaypartTransformerProps, IIsDayPartOutOfTimeIntervalProps } from './types';

export const separateBreakfastMenu = (menu: IMainMenuViewOption[]) => {
  const menuSections = menu.filter(val => val?._type === MenuObjectTypes.SECTION);

  const breakfastMenuData = menuSections.filter((val: IMainMenuSectionView) =>
    val?.daypart?.includes(DAY_PART_SELECTIONS.BREAKFAST_KEY)
  );

  const menuData = menuSections.filter(
    (val: IMainMenuSectionView) => !val?.daypart?.includes(DAY_PART_SELECTIONS.BREAKFAST_KEY)
  );

  return { menuData, breakfastMenuData };
};

export const daypartDaytimeTransformer = ({
  key,
  label,
  formattedDaytimeMessage,
}: IDaypartTransformerProps): string =>
  key === DAY_PART_SELECTIONS.BREAKFAST_KEY ? label : formattedDaytimeMessage;

export const excludeDisabledMenuSections = ({
  menuData,
  activeDayParts,
  showStaticMenu,
  showBurgersForBreakfast,
  serviceMode,
}: {
  menuData?: IMainMenuViewOption[];
  activeDayParts: IDayPartBoundary[];
  showStaticMenu?: boolean;
  showBurgersForBreakfast?: boolean;
  serviceMode: ServiceMode | null;
}) => {
  const activeDayPartsSet = new Set(activeDayParts.map(dayPart => dayPart.key.toLowerCase()));
  return menuData?.filter((menuOption: IMainMenuSectionView) => {
    if (menuOption.hiddenFromMainMenu) {
      return false;
    }

    if (showStaticMenu) {
      return menuOption.showInStaticMenu;
    }

    if (menuOption.channelExclusions) {
      const shouldDisplayOption = filterWithChannelExclusions(
        menuOption.channelExclusions,
        serviceMode
      );

      // Only return when item should be hidden, otherwise continue with the rest of the checks
      if (!shouldDisplayOption) {
        return false;
      }
    }

    if (menuOption._type !== MenuObjectTypes.SECTION || !menuOption?.daypart?.length) {
      return true;
    }

    // Burgers for Breakfast
    if (
      menuOption.daypart.includes(DAY_PART_SELECTIONS.BURGERS_FOR_BREAKFAST) &&
      activeDayPartsSet.has(DAY_PART_SELECTIONS.BURGERS_FOR_BREAKFAST.toLowerCase())
    ) {
      return showBurgersForBreakfast;
    }
    return menuOption.daypart.some(dayPart => activeDayPartsSet.has(dayPart.toLowerCase()));
  });
};

export const isDayPartOutOfTimeInterval = ({
  currentUserTime,
  interval,
}: IIsDayPartOutOfTimeIntervalProps) =>
  (interval?.startTime && isBefore(currentUserTime, interval.startTime)) ||
  (interval?.endTime && isAfter(currentUserTime, interval.endTime));

export const filterWithChannelExclusions = (
  exclusions: IChannelExclusions,
  serviceMode: ServiceMode | null
): boolean => {
  if (!exclusions) {
    return true;
  }

  if (exclusions.kiosk) {
    return !isKiosk;
  }

  if (exclusions.pickup) {
    return !isPickup(serviceMode);
  }

  if (exclusions.delivery) {
    return serviceMode !== ServiceMode.DELIVERY;
  }

  return true;
};

export const usePrefetchMenuSections = () => {
  const enableIapPrefetching = useFlag(LaunchDarklyFlag.ENABLE_IAP_PREFETCHING);

  // Prefetch menu sections
  const { getMainMenu, getMenuObject } = useMenuContext();
  const { data: mainMenuData } = getMainMenu();

  const menuSectionIds = useMemo(
    () =>
      enableIapPrefetching
        ? mainMenuData?.options.map(({ _type, _id }) => `${_type}-${_id}`) ?? []
        : [],
    [enableIapPrefetching, mainMenuData]
  );

  useEffect(() => {
    if (enableIapPrefetching && getMenuObject) {
      menuSectionIds.forEach(getMenuObject);
    }
    /**
     * JSON.stringify deep equality check so data is only
     * prefetched when section IDs change. Should be fine,
     * performance-wise since menuSectionIds relatively shallow,
     * doesn't have cycles, and is easily serializable.
     *
     * https://github.com/facebook/react/issues/14476#issuecomment-471199055
     * */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableIapPrefetching, getMenuObject, JSON.stringify(menuSectionIds)]);
};
